export const COMMAND_CENTER_CATEGORY_ORDER = ['launch', 'people', 'content', 'system', 'revenue'];

const CATEGORY_LABELS = {
  launch: 'Launch',
  people: 'People',
  content: 'Content',
  system: 'System',
  revenue: 'Revenue',
};

const WORKSPACE_LABELS = {
  monitoring: 'Monitoring',
  marketing: 'Marketing',
  'free-users': 'Free Users',
  'heart-work': 'Dad Partners',
  communication: 'Prospects',
  'guide-partners': 'Guide Partners',
  'advisory-partners': 'Advisory Partners',
  'support-partners': 'Support Partners',
  'super-fans': 'Super Fans',
  stewardship: 'Operations',
};

const STATE_LABELS = {
  needs_review: 'Needs review',
  active: 'Active',
  later: 'Later',
  resolved: 'Resolved',
};

const WORKSPACE_TARGET_DEFAULTS = {
  monitoring: { roomId: 'system-health', tab: 'monitoring' },
  marketing: { roomId: 'marketing', tab: 'marketingcontent' },
  'free-users': { roomId: 'free-users', tab: 'freeuserscomingsoon' },
  communication: { roomId: 'reach-dads', tab: 'members' },
  'guide-partners': { roomId: 'guide-partners', tab: 'guestguides' },
  'advisory-partners': { roomId: 'advisory-partners', tab: 'advisorycontent' },
  'support-partners': { roomId: 'support-partners', tab: 'supportpartneraccounts' },
  'super-fans': { roomId: 'super-fans', tab: 'superfanaccounts' },
  'heart-work': { roomId: 'support-dads', tab: 'members' },
  stewardship: { roomId: 'steward-the-work', tab: 'analytics' },
};

export function getCommandCenterCategoryLabel(category) {
  return CATEGORY_LABELS[category] || 'Unsorted';
}

export function getCommandCenterWorkspaceLabel(workspace) {
  return WORKSPACE_LABELS[workspace] || 'Command Center';
}

export function getCommandCenterStateLabel(state) {
  return STATE_LABELS[state] || 'Queue';
}

export function formatQueueDateTime(value) {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeQueueText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

export function getSlackCaptureCardCopy(item) {
  const title = normalizeQueueText(item?.title);
  const detail = normalizeQueueText(item?.detail);
  const fallback = {
    title: title || detail || 'Untitled action',
    preview: detail || item?.rank_reason || 'No additional context yet.',
  };

  if (item?.source_type !== 'slack') {
    return fallback;
  }

  const sourceText = detail || title;
  if (!sourceText) {
    return fallback;
  }

  const sentenceMatch = sourceText.match(/^(.+?[.!?])(\s+.+)?$/);
  const firstSentence = normalizeQueueText(sentenceMatch?.[1] || sourceText);
  const remainder = normalizeQueueText(sentenceMatch?.[2] || '');

  return {
    title: firstSentence || fallback.title,
    preview: remainder || (sourceText !== firstSentence ? sourceText : fallback.preview),
  };
}

export function buildQueueCategoryGroups(queueState) {
  const grouped = Object.fromEntries(
    COMMAND_CENTER_CATEGORY_ORDER.map((category) => [category, []])
  );

  const items = [
    ...(Array.isArray(queueState?.needs_review) ? queueState.needs_review : []),
    ...(Array.isArray(queueState?.active) ? queueState.active : []),
    ...(Array.isArray(queueState?.later) ? queueState.later : []),
  ];

  items.forEach((item) => {
    const category = COMMAND_CENTER_CATEGORY_ORDER.includes(item?.category) ? item.category : null;
    if (!category) return;
    grouped[category].push(item);
  });

  return COMMAND_CENTER_CATEGORY_ORDER.map((category) => ({
    id: category,
    label: getCommandCenterCategoryLabel(category),
    items: grouped[category],
    counts: grouped[category].reduce((counts, item) => {
      const state = item?.state || 'active';
      counts.total += 1;
      counts[state] = (counts[state] || 0) + 1;
      return counts;
    }, { total: 0, needs_review: 0, active: 0, later: 0 }),
  }));
}

export function getQueueTargetDefaultsForWorkspace(workspace) {
  return WORKSPACE_TARGET_DEFAULTS[workspace] || null;
}

export function getQueueItemNavigation(item) {
  if (!item) return null;

  const fallback = getQueueTargetDefaultsForWorkspace(item.workspace);
  const normalizedAnchor = String(item.target_anchor || '').trim().toLowerCase();
  const hasPreciseContext = Boolean(
    normalizedAnchor
    || item.target_record_id
    || item.highlight_mode
  );

  let roomId = item.target_room_id || null;
  let tab = item.target_tab || null;

  if ((!roomId || !tab) && hasPreciseContext && fallback) {
    roomId = roomId || fallback.roomId;
    tab = tab || fallback.tab;
  }

  if (!roomId || !tab) return null;

  const workflowKey = tab === 'guestguides' && ['guide-partner-outreach', 'outreach'].includes(normalizedAnchor)
    ? 'guide-partner-outreach'
    : null;
  const isGenericWorkspaceTarget = Boolean(
    fallback
    && roomId === fallback.roomId
    && tab === fallback.tab
    && !hasPreciseContext
  );

  if (isGenericWorkspaceTarget && (item.source_type === 'manual' || item.source_type === 'slack')) {
    return null;
  }

  return {
    roomId,
    tab,
    toolContext: {
      workflowKey,
      targetAnchor: item.target_anchor || null,
      targetRecordId: item.target_record_id || null,
      highlightMode: item.highlight_mode || null,
      queueItemId: item.queue_item_id || null,
    },
  };
}
